import { useMemo, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { fetchAdminAudit, toUnifiedFromAdminAudit } from '@/api/console';
import { auditOrderingParam } from './auditFilterTypes';
import { resolveDateRange } from './filters';
import { useAuditFilters } from './useAuditFilters';
import { AuditPanelShell } from './AuditPanelShell';

/**
 * EAA-1821: the "Quản trị" source — server-paginated `AdminAudit` via
 * `GET /api/v1/admin/audit`, IsSuperAdmin-gated.
 *
 * The old docstring here recorded a DEVIATION: this source had no free-text
 * target search because the backend only matched `target_type` exactly, and a
 * box that silently no-ops is worse than no box. That constraint is gone — the
 * view now takes `q`, `target_id`, `actor`, `from`/`to` and multi-value
 * `action`/`target_type`, all server-side, so the free-text search and the
 * per-column target search below are real filters and "Hiển thị N / total"
 * stays honest.
 */
export function AdminAuditPanel(): ReactElement | null {
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const model = useAuditFilters();
    const { debouncedFilters, sort, page } = model;

    const params = useMemo(() => {
        const range = resolveDateRange(debouncedFilters.date);
        return {
            page,
            q: debouncedFilters.search.trim() || undefined,
            action: debouncedFilters.action,
            success: debouncedFilters.success,
            target_type: debouncedFilters.targetType,
            target_id: debouncedFilters.target.trim() || undefined,
            actor: debouncedFilters.actor.trim() || undefined,
            from: range.from,
            to: range.to,
            ordering: auditOrderingParam(sort),
        };
    }, [debouncedFilters, sort, page]);

    const enabled = authenticated && Boolean(accessToken) && can('console.audit.view');
    const query = useQuery({
        queryKey: ['admin-audit', params, accessToken],
        queryFn: () => fetchAdminAudit(config.apiBase, accessToken as string, params),
        enabled,
        retry: false,
    });

    const data = useMemo(
        () => ({
            rows: query.data?.results.map(toUnifiedFromAdminAudit) ?? [],
            total: query.data?.count ?? 0,
            totalPages: query.data?.total_pages ?? 1,
            pageSize: query.data?.page_size ?? 20,
        }),
        [query.data]
    );

    if (!can('console.audit.view')) {
        return null;
    }

    return <AuditPanelShell kind="admin" model={model} data={data} query={query} />;
}
